import { RegistrationCertificateDownloadButton } from "@/components/license-details/registration-certificate-download-button";
import { RegistrationStatusInfoButton } from "@/components/registration-status-info-panel";
import { Button } from "@/components/ui/button";
import { CardContent, CardFooter } from "@/components/ui/card";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { toast } from "@/components/ui/sonner";
import { useAppForm } from "@/components/ui/tanstack-form";
import { UserType } from "@/constants";
import {
  deriveLicenseFromPaymentsMutation,
  updateLicenseMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { LicenseResponseSchema } from "@/hera-api/client/types.gen";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import { getLinkedLicenseDetailsTarget } from "@/lib/linked-license-details-link";
import {
  LICENSE_STATUS_VALUES,
  VALID_LICENSE_STATUS_VALUES_FOR_EDIT,
  formatDateNoTimezone,
  formatDateTime,
  getInspectionStatusDisplay,
  getLicenseStatusEditOptionLabel,
  getQueryErrorMessage,
  getRegistrationStatusDisplay,
  inspectionPaymentsHasAwaitingCheckPayment,
  inspectionPaymentsHasAwaitingInvoicePayment,
  newestInspectionForListStatus,
  shouldShowInspectionStatusOnLicenseList,
} from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { Calculator, RotateCcw, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { QuestionField } from "../../registrant/form-utils/question-field-utils";
import { Badge } from "../../ui/badge";
import { LicenseInfoSchema } from "../schemas/validation-schemas";

function licenseShowsServiceTransferDate(
  license: Pick<
    LicenseResponseSchema,
    | "transferred_from_license_id"
    | "is_omt_change"
    | "transfer_in_date"
    | "effective_date"
  >,
): boolean {
  if (!license.transfer_in_date) {
    return false;
  }
  if (license.transferred_from_license_id || license.is_omt_change) {
    return true;
  }
  return license.transfer_in_date !== license.effective_date;
}

function ServiceTransferDateRow({
  transferInDate,
}: {
  transferInDate: string;
}) {
  return (
    <div>
      <p className="text-sm font-bold">Service Transfer Date</p>
      <p className="text-sm">{formatDateNoTimezone(transferInDate)}</p>
    </div>
  );
}

interface LicenseInfoCardContentProps {
  data: LicenseResponseSchema;
  userType: UserType;
  licenseId: string;
  forceEdit?: boolean;
  /** When there is no property card, show the registration certificate action here. */
  showRegistrationCertificateButton?: boolean;
}

interface EditableLicenseInfoProps {
  data: LicenseResponseSchema;
  licenseId: string;
  onSave: () => void;
  onReset: () => void;
  showRegistrationCertificateButton?: boolean;
}

function EditableLicenseInfo({
  data,
  licenseId,
  onSave,
  onReset,
  showRegistrationCertificateButton = false,
}: EditableLicenseInfoProps) {
  const queryClient = useQueryClient();
  const firstInputRef = useRef<HTMLInputElement>(null);

  const updateLicense = useMutation({
    ...updateLicenseMutation(),
  });

  const deriveFromPayments = useMutation({
    ...deriveLicenseFromPaymentsMutation(),
  });

  useEffect(() => {
    // Focus on the first input when the component mounts
    // Use setTimeout to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const form = useAppForm({
    defaultValues: {
      status: data.status,
      renewal_date: data.renewal_date || null,
      effective_date: data.effective_date || null,
      effective_end_date: data.effective_end_date || null,
      approved_at: data.approved_at ? data.approved_at.slice(0, 10) : null,
      approved_until_date: data.approved_until_date || null,
      cancellation_date: data.cancellation_date || null,
      cancellation_reason: data.cancellation_reason || null,
    },
    validators: {
      onSubmit: LicenseInfoSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateLicense.mutateAsync({
          path: { license_id: licenseId },
          body: {
            ...value,
            approved_at: value.approved_at
              ? `${value.approved_at}T00:00:00.000Z`
              : null,
          },
        });
        await notifyLicenseDetailsChanged(queryClient, licenseId);
        toast({
          title: "Success",
          description: "License information updated successfully",
          variant: "secondary",
        });
        onSave();
      } catch (error) {
        toast({
          title: "Error",
          description: `${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
      }
    },
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  const handleReset = () => {
    form.reset();
    onReset();
  };

  const handleSetBasedOnPayments = async () => {
    try {
      const derived = await deriveFromPayments.mutateAsync({
        path: { license_id: licenseId },
      });
      form.setFieldValue("status", derived.status);
      form.setFieldValue("renewal_date", derived.renewal_date ?? null);
      form.setFieldValue(
        "approved_until_date",
        derived.approved_until_date ?? null,
      );
      form.setFieldValue(
        "approved_at",
        derived.approved_at ? derived.approved_at.slice(0, 10) : null,
      );
      toast({
        title: "Fields updated",
        description:
          "Renewal, approval, and status were calculated from payments. Review and save to apply.",
        variant: "secondary",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  };

  const isBusy = updateLicense.isPending || deriveFromPayments.isPending;

  return (
    <CardContent>
      <form.AppForm>
        <form className="space-y-4" onSubmit={handleSave}>
          <div className="grid gap-4 grid-cols-1 @min-[450px]:grid-cols-2 items-start">
            <div>
              <p className="text-sm font-bold">ID</p>
              <p className="text-sm">{data.display_id}</p>
            </div>

            <div>
              <p className="text-sm font-bold">License Type</p>
              <p className="text-sm">{data.license_type?.name || "N/A"}</p>
            </div>

            <form.AppField name="status">
              {(field) => {
                // Get valid statuses for editing
                const validStatuses = LICENSE_STATUS_VALUES.filter((status) =>
                  VALID_LICENSE_STATUS_VALUES_FOR_EDIT.includes(status as any),
                );

                // Ensure current value is included even if not in valid list
                const currentValue = field.state.value as string;
                const allStatuses = new Set(validStatuses);
                if (currentValue && !allStatuses.has(currentValue as any)) {
                  allStatuses.add(currentValue as any);
                }

                // Map to options with user-friendly labels
                const statusDisplayContext = {
                  renewalDate: data.renewal_date,
                  isRenewable: data.is_renewable,
                  hasAwaitingCheckPayment: (
                    data as { has_awaiting_check_payment?: boolean }
                  ).has_awaiting_check_payment,
                  cancellationReason: data.cancellation_reason,
                  hasAwaitingInvoicePayment: (
                    data as { has_awaiting_invoice_payment?: boolean }
                  ).has_awaiting_invoice_payment,
                };
                const answerOptions = Array.from(allStatuses).map((status) => ({
                  value: status,
                  label: getLicenseStatusEditOptionLabel(
                    status,
                    statusDisplayContext,
                  ),
                }));

                return (
                  <FormFieldWrapper field={field} label="Status">
                    <QuestionField
                      answerType="single_select"
                      field={field}
                      answerOptions={answerOptions}
                    />
                  </FormFieldWrapper>
                );
              }}
            </form.AppField>

            <form.AppField
              name="cancellation_reason"
              validators={{
                onChange: LicenseInfoSchema.shape.cancellation_reason,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Cancellation Reason">
                  <QuestionField
                    answerType="text"
                    field={field}
                    ref={firstInputRef}
                  />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="effective_date"
              validators={{
                onChange: LicenseInfoSchema.shape.effective_date,
              }}
            >
              {(field) => (
                <FormFieldWrapper
                  field={field}
                  label={
                    data.license_type?.effective_date_label || "Effective Date"
                  }
                >
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>

            {licenseShowsServiceTransferDate(data) ? (
              <ServiceTransferDateRow transferInDate={data.transfer_in_date} />
            ) : null}

            <div>
              <p className="text-sm font-bold">Application Date</p>
              <p className="text-sm">
                {formatDateTime(data.created_at) || "N/A"}
              </p>
            </div>

            <form.AppField
              name="renewal_date"
              validators={{
                onChange: LicenseInfoSchema.shape.renewal_date,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Renewal Date">
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="approved_at"
              validators={{
                onChange: LicenseInfoSchema.shape.approved_at,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Approval Date">
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="approved_until_date"
              validators={{
                onChange: LicenseInfoSchema.shape.approved_until_date,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Approved Until Date">
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <div className="hidden @min-[450px]:block" aria-hidden="true" />

            <form.AppField
              name="effective_end_date"
              validators={{
                onChange: LicenseInfoSchema.shape.effective_end_date,
              }}
            >
              {(field) => (
                <FormFieldWrapper
                  field={field}
                  label={
                    data.license_type?.effective_end_date_label ||
                    "Effective End Date"
                  }
                >
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>

            <form.AppField
              name="cancellation_date"
              validators={{
                onChange: LicenseInfoSchema.shape.cancellation_date,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Cancellation Date">
                  <QuestionField answerType="date" field={field} />
                </FormFieldWrapper>
              )}
            </form.AppField>
          </div>

          <CardFooter className="flex justify-between pt-2 px-0">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isBusy}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSetBasedOnPayments}
                disabled={isBusy}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Set based on payments
              </Button>
            </div>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isBusy}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting || updateLicense.isPending
                    ? "Saving..."
                    : "Save"}
                </Button>
              )}
            </form.Subscribe>
          </CardFooter>
          {showRegistrationCertificateButton ? (
            <div className="pt-4 border-t">
              <RegistrationCertificateDownloadButton
                licenseId={licenseId}
                className="w-full sm:w-auto"
              />
            </div>
          ) : null}
        </form>
      </form.AppForm>
    </CardContent>
  );
}

interface ReadOnlyLicenseInfoProps {
  data: LicenseResponseSchema;
  userType: UserType;
  licenseId: string;
  showRegistrationCertificateButton?: boolean;
}

function ReadOnlyLicenseInfo({
  data,
  userType,
  licenseId,
  showRegistrationCertificateButton = false,
}: ReadOnlyLicenseInfoProps) {
  const registrationDisplay = getRegistrationStatusDisplay(
    data.status,
    data.renewal_date,
    (data as { has_awaiting_check_payment?: boolean })
      ?.has_awaiting_check_payment,
    data.cancellation_reason,
    (data as { has_awaiting_invoice_payment?: boolean })
      ?.has_awaiting_invoice_payment,
    data.is_renewable,
    userType === UserType.REGISTRANT,
    data.total_due,
  );
  const newestInspection = newestInspectionForListStatus(data.inspections);
  const inspectionDisplay =
    newestInspection &&
    shouldShowInspectionStatusOnLicenseList(
      data.status,
      registrationDisplay.label,
    )
      ? getInspectionStatusDisplay(
          newestInspection.status,
          inspectionPaymentsHasAwaitingCheckPayment(newestInspection.payments),
          data.status !== "PAID_PENDING_REVIEW",
          inspectionPaymentsHasAwaitingInvoicePayment(
            newestInspection.payments,
          ),
        )
      : null;
  const statusDisplay = inspectionDisplay ?? registrationDisplay;

  const badge = (
    <Badge variant={statusDisplay.variant}>{statusDisplay.label}</Badge>
  );
  const licenseType = data.license_type;
  const pathname = useLocation().pathname;
  const linkedLicenses = data.linked_licenses?.length
    ? data.linked_licenses
    : (data.linked_license_display_ids ?? []).map((display_id) => ({
        id: "",
        display_id,
      }));

  return (
    <CardContent>
      <div className="grid gap-4 grid-cols-1 @min-[450px]:grid-cols-2 items-start">
        <div>
          <p className="text-sm font-bold">ID</p>
          <p className="text-sm">{data.display_id}</p>
        </div>

        <div>
          <p className="text-sm font-bold">License Type</p>
          <p className="text-sm">{licenseType?.name || "N/A"}</p>
        </div>

        {data.is_omt_change ? (
          <div>
            <p className="text-sm font-bold">OMT change</p>
            <p className="text-sm">Yes (fees from transfer-in date)</p>
          </div>
        ) : null}

        <div>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-bold">Status</p>
            {(userType === UserType.OPERATOR ||
              userType === UserType.GOVERNMENT) && (
              <RegistrationStatusInfoButton />
            )}
          </div>
          <p className="text-sm">{badge}</p>
          {data.status === "CANCELLED_REQUIRES_PAYMENT" &&
          data.cancellation_reason ? (
            <p className="text-xs text-muted-foreground mt-0.5">
              {data.cancellation_reason}
            </p>
          ) : null}
        </div>

        <div>
          <p className="text-sm font-bold">Cancellation Reason</p>
          <p className="text-sm">{data.cancellation_reason || "N/A"}</p>
        </div>

        <div>
          <p className="text-sm font-bold">
            {licenseType?.effective_date_label || "Effective Date"}
          </p>
          <p className="text-sm">{formatDateNoTimezone(data.effective_date)}</p>
        </div>

        {licenseShowsServiceTransferDate(data) ? (
          <ServiceTransferDateRow transferInDate={data.transfer_in_date} />
        ) : null}

        <div>
          <p className="text-sm font-bold">Application Date</p>
          <p className="text-sm">{formatDateTime(data.created_at) || "N/A"}</p>
        </div>

        <div>
          <p className="text-sm font-bold">Renewal Date</p>
          <p className="text-sm">{formatDateNoTimezone(data.renewal_date)}</p>
        </div>

        <div>
          <p className="text-sm font-bold">Approval Date</p>
          <p className="text-sm">
            {data.approved_at ? formatDateTime(data.approved_at) : "N/A"}
          </p>
        </div>

        {userType !== UserType.REGISTRANT ? (
          <>
            <div>
              <p className="text-sm font-bold">Approved Until Date</p>
              <p className="text-sm">
                {formatDateNoTimezone(data.approved_until_date) || "N/A"}
              </p>
            </div>

            <div className="hidden @min-[450px]:block" aria-hidden="true" />

            <div>
              <p className="text-sm font-bold">
                {licenseType?.effective_end_date_label || "Effective End Date"}
              </p>
              <p className="text-sm">
                {formatDateNoTimezone(data.effective_end_date) || "N/A"}
              </p>
            </div>

            <div>
              <p className="text-sm font-bold">Cancellation Date</p>
              <p className="text-sm">
                {formatDateNoTimezone(data.cancellation_date) || "N/A"}
              </p>
            </div>
          </>
        ) : data.status === "CANCELLED" ? (
          <>
            <div className="hidden @min-[450px]:block" aria-hidden="true" />
            <div>
              <p className="text-sm font-bold">Cancellation Date</p>
              <p className="text-sm">
                {formatDateNoTimezone(data.cancellation_date) || "N/A"}
              </p>
            </div>
          </>
        ) : null}

        {linkedLicenses.length ? (
          <div className="@min-[450px]:col-span-2">
            <p className="text-sm font-bold">Linked licenses</p>
            <p className="text-sm">
              {linkedLicenses.map((linked, index) => {
                const separator = index > 0 ? ", " : null;
                if (!linked.id) {
                  return (
                    <span key={linked.display_id}>
                      {separator}
                      {linked.display_id}
                    </span>
                  );
                }
                const target = getLinkedLicenseDetailsTarget(
                  userType,
                  pathname,
                  linked.id,
                );
                return (
                  <span key={linked.id}>
                    {separator}
                    <Link
                      to={target.to as never}
                      params={target.params as never}
                      search={target.search as never}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {linked.display_id}
                    </Link>
                  </span>
                );
              })}
            </p>
          </div>
        ) : null}
      </div>
      {showRegistrationCertificateButton ? (
        <div className="pt-4 mt-4 border-t">
          <RegistrationCertificateDownloadButton
            licenseId={licenseId}
            className="w-full sm:w-auto"
          />
        </div>
      ) : null}
    </CardContent>
  );
}

export function LicenseInfoCardContent({
  data,
  userType,
  licenseId,
  forceEdit,
  showRegistrationCertificateButton = false,
}: LicenseInfoCardContentProps) {
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (userType === UserType.OPERATOR && forceEdit && !isEditing) {
      setIsEditing(true);
    }
  }, [forceEdit, isEditing, userType]);

  if (userType === UserType.OPERATOR && isEditing) {
    return (
      <EditableLicenseInfo
        data={data}
        licenseId={licenseId}
        onSave={() => setIsEditing(false)}
        onReset={() => setIsEditing(false)}
        showRegistrationCertificateButton={showRegistrationCertificateButton}
      />
    );
  }

  return (
    <ReadOnlyLicenseInfo
      data={data}
      userType={userType}
      licenseId={licenseId}
      showRegistrationCertificateButton={showRegistrationCertificateButton}
    />
  );
}
